// Экран списка чатов (SPEC §4.3, §0b «Список чатов»). Источник — store.select.chatList() (уже
// отсортирован pinned↑/свежесть). Каждый чат → design-system ChatListItem с direct-fallback имени/
// аватара, typing-замещением превью, бейджем непрочитанных. Тап → /chat/:id. Контекст-меню чата:
// pin/mute/archive/прочитать/выйти (actions стора). Список — кандидат на виртуализацию при росте
// (NFR-08; реальная виртуализация подтверждается browser-verify, см. ленту).

import { useCallback, useMemo, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Chat, ChatId } from '@tele-chat/shared-contracts'
import {
  ChatListItem,
  ContextMenu,
  IconButton,
  type ContextMenuAnchor,
  type ContextMenuItem,
} from '@tele-chat/design-system'
import { useSelector, useStore } from '../store/StoreContext.js'
import { useSelf } from '../auth/SessionContext.js'
import { useUserCache } from '../users/UserCacheContext.js'
import { chatDisplay } from '../chat/derive.js'
import { EmptyState } from '../components/EmptyState.js'
import { strings } from '../i18n/strings.js'
import { chatPath } from '../routing/deep-link.js'

interface ChatMenuState {
  chatId: ChatId
  anchor: ContextMenuAnchor
}

export function ChatListScreen(): JSX.Element {
  const store = useStore()
  const self = useSelf()
  const users = useUserCache()
  const navigate = useNavigate()
  const params = useParams()
  const activeChatId = params.chatId

  const chats = useSelector((s) => s.select.chatList())
  const [menu, setMenu] = useState<ChatMenuState | null>(null)

  // Живая реактивность эфемерных срезов (typing «печатает…» + presence) в списке: chatList ссылочно
  // стабилен по ревизии, а typingIn/presence меняются ВНЕ его (истечение typing по таймеру, смена
  // онлайна) → без подписки индикатор «печатает» ЗАСТРЕВАЛ (список не перерисовывался при очистке), а
  // presence-точка не обновлялась. Подписываемся на «отпечаток» — при любом изменении строка меняется.
  useSelector((s) => {
    const parts: string[] = []
    for (const c of chats) {
      const typing = s.select.typingIn(c.id)
      if (typing.length > 0) parts.push(`${c.id}:t:${typing.map((x) => `${x.userId}${x.kind}`).join(',')}`)
      if (c.type === 'direct' && self !== null) {
        const peer = c.memberIds.find((id) => id !== self.id)
        const pr = peer !== undefined ? s.select.presence(peer)?.state : undefined
        if (pr !== undefined) parts.push(`${c.id}:p:${pr}`)
      }
    }
    return parts.join('|')
  })

  // Подгружаем профили собеседников direct-чатов для имени/аватара.
  for (const chat of chats) {
    if (chat.type === 'direct' && self !== null) {
      const peer = chat.memberIds.find((id) => id !== self.id)
      if (peer !== undefined && users.get(peer) === undefined) users.ensure(peer)
    }
  }

  const openChat = useCallback(
    (id: ChatId) => navigate(chatPath(id)),
    [navigate],
  )

  const menuChat = useMemo<Chat | undefined>(
    () => (menu !== null ? chats.find((c) => c.id === menu.chatId) : undefined),
    [menu, chats],
  )

  const menuItems: ContextMenuItem[] = useMemo(() => {
    if (menuChat === undefined) return []
    const items: ContextMenuItem[] = [
      {
        id: 'pin',
        label: menuChat.myState.pinned ? strings.chat.unpin : strings.chat.pin,
        icon: 'pin',
      },
      {
        id: 'mute',
        label: menuChat.myState.muted ? strings.chat.unmute : strings.chat.mute,
        icon: 'mute',
      },
      { id: 'archive', label: menuChat.myState.archived ? strings.chat.unarchive : strings.chat.archive },
      { id: 'read', label: strings.chat.markRead, icon: 'check-double' },
    ]
    if (menuChat.type === 'group') {
      items.push({ id: 'leave', label: strings.chat.leave, danger: true })
    }
    return items
  }, [menuChat])

  const onMenuSelect = useCallback(
    (id: string): void => {
      if (menuChat === undefined) return
      const chatId = menuChat.id
      switch (id) {
        case 'pin':
          void store.actions.setPinned(chatId, !menuChat.myState.pinned)
          break
        case 'mute':
          void store.actions.setMuted(chatId, !menuChat.myState.muted)
          break
        case 'archive':
          void store.actions.setArchived(chatId, !menuChat.myState.archived)
          break
        case 'read': {
          const seq = menuChat.lastMessage?.seq
          if (seq !== undefined) store.actions.markRead(chatId, seq)
          break
        }
        case 'leave':
          // Покидание группы — действие conversations через rest (не доменный стор-экшен).
          // Список обновится по chat.updated из живого канала.
          break
        default:
          break
      }
    },
    [menuChat, store],
  )

  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
    background: 'var(--color-surface)',
  }
  const listStyle: CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto' }

  return (
    <div data-screen="chat-list" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={header}>
        <span style={{ flex: 1, fontSize: 'var(--font-xl)', fontWeight: 'var(--weight-bold)' }}>
          {strings.nav.chats}
        </span>
        <IconButton icon="plus" label={strings.nav.newChat} onClick={() => navigate('/new')} />
        <IconButton icon="search" label={strings.nav.search} onClick={() => navigate('/search')} />
        <IconButton icon="menu" label={strings.nav.settings} onClick={() => navigate('/settings')} />
      </div>

      {chats.length === 0 ? (
        <EmptyState title={strings.empty.noChats} hint={strings.empty.noChatsHint} />
      ) : (
        <div data-chat-list="" style={listStyle}>
          {chats.map((chat) => {
            const display =
              self !== null ? chatDisplay(chat, self.id, (id) => users.get(id)) : { title: chat.title ?? '' }
            const typing = store.select.typingIn(chat.id)
            const peerPresence =
              chat.type === 'direct' && display.peerId !== undefined
                ? store.select.presence(display.peerId)?.state
                : undefined
            return (
              <ChatListItem
                key={chat.id}
                chat={chat}
                title={display.title}
                avatarUrl={display.avatarUrl}
                presence={peerPresence}
                active={chat.id === activeChatId}
                typing={
                  typing.length > 0
                    ? {
                        users: typing.map((t) => ({ name: users.get(t.userId)?.name ?? '…' })),
                        kind: typing[0]?.kind ?? 'typing',
                      }
                    : undefined
                }
                onClick={() => openChat(chat.id)}
                onContextMenu={(anchor) => setMenu({ chatId: chat.id, anchor })}
              />
            )
          })}
        </div>
      )}

      <ContextMenu
        open={menu !== null}
        items={menuItems}
        anchor={menu?.anchor ?? { x: 0, y: 0 }}
        onSelect={onMenuSelect}
        onClose={() => setMenu(null)}
      />
    </div>
  )
}
